import { useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Mail,
  Phone,
  MapPin,
  Building2,
  Calendar,
  FileText,
  Pencil,
  X,
  Loader2,
  Plus,
  FolderKanban,
  Upload,
  Download,
  Trash2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { formatDate } from "@/lib/utils";
import { adminFetch } from "@/lib/api";
import { useFetch } from "@/hooks/use-fetch";
import { PageLoading } from "@/components/loading";
import { ErrorMessage } from "@/components/error-message";
import { PROGRAM_LABELS, PROJECT_STATUS } from "@/lib/constants";

interface Project {
  id: number;
  title: string;
  program_type: string;
  status: string;
  created_at: string;
}

interface Client {
  id: number;
  company_name: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  tax_number: string;
  city: string;
  district: string;
  sector: string;
  notes: string;
  client_type?: string;
  logo_r2_key: string | null;
  stamp_r2_key: string | null;
  logo_url?: string | null;
  stamp_url?: string | null;
  created_at: string;
  projects?: Project[];
}

interface ClientResponse {
  data: Client;
}

const CLIENT_TYPES = [
  { value: "potential", label: "Potansiyel" },
  { value: "active", label: "Aktif" },
];

const ACCEPTED_IMAGE_TYPES = ".jpg,.jpeg,.png,.svg,.webp";

interface ClientDocument {
  id: string;
  name: string;
  size: number;
  uploadedAt: string;
  url: string;
}

export function ClientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [form, setForm] = useState({
    company_name: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    tax_number: "",
    city: "",
    district: "",
    sector: "",
    notes: "",
    client_type: "potential",
  });

  // Logo & Stamp upload state
  const [logoUploading, setLogoUploading] = useState(false);
  const [stampUploading, setStampUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const stampInputRef = useRef<HTMLInputElement>(null);

  // Client documents state
  const [clientDocs] = useState<ClientDocument[]>([]);
  const [docDragOver, setDocDragOver] = useState(false);
  const docInputRef = useRef<HTMLInputElement>(null);

  const { data, loading, error, refetch } = useFetch<ClientResponse>(
    `/clients/${id}`
  );

  function startEditing() {
    if (!data) return;
    const c = data.data;
    setForm({
      company_name: c.company_name || "",
      contact_name: c.contact_name || "",
      contact_email: c.contact_email || "",
      contact_phone: c.contact_phone || "",
      tax_number: c.tax_number || "",
      city: c.city || "",
      district: c.district || "",
      sector: c.sector || "",
      notes: c.notes || "",
      client_type: c.client_type || "potential",
    });
    setEditing(true);
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!id) return;
    setSaving(true);
    try {
      await adminFetch(`/clients/${id}`, {
        method: "PATCH",
        body: JSON.stringify(form),
      });
      setEditing(false);
      refetch();
    } catch {
      // Error handled via UI
    } finally {
      setSaving(false);
    }
  }

  function handleFormChange(field: string, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleDeleteClient() {
    if (!id) return;
    setDeleting(true);
    setDeleteError("");
    try {
      await adminFetch(`/clients/${id}`, { method: "DELETE" });
      navigate("/clients");
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "Silme işlemi başarısız. Müşteriye ait projeler olabilir.";
      setDeleteError(message);
      setDeleting(false);
    }
  }

  function getUploadHeaders(): Record<string, string> {
    const headers: Record<string, string> = {};
    if (import.meta.env.DEV) headers["X-Admin-Email"] = "abdulahcicekli@gmail.com";
    const cfMatch = document.cookie.match(/CF_Authorization=([^;]+)/);
    if (cfMatch) headers["Cf-Access-Jwt-Assertion"] = cfMatch[1];
    return headers;
  }

  function getApiBase(): string {
    return import.meta.env.VITE_API_URL
      ? `${import.meta.env.VITE_API_URL}/api/admin`
      : "/api/admin";
  }

  async function handleLogoSelect(file: File | null) {
    if (!file || !id) return;
    setLogoUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch(`${getApiBase()}/clients/${id}/logo`, {
        method: "POST",
        headers: getUploadHeaders(),
        body: formData,
      });
      if (!res.ok) throw new Error("Yükleme başarısız");
      setUploadSuccess("Logo başarıyla yüklendi");
      setTimeout(() => setUploadSuccess(null), 3000);
      refetch();
    } catch {
      // Hata durumu
    } finally {
      setLogoUploading(false);
    }
  }

  async function handleStampSelect(file: File | null) {
    if (!file || !id) return;
    setStampUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch(`${getApiBase()}/clients/${id}/stamp`, {
        method: "POST",
        headers: getUploadHeaders(),
        body: formData,
      });
      if (!res.ok) throw new Error("Yükleme başarısız");
      setUploadSuccess("Kaşe başarıyla yüklendi");
      setTimeout(() => setUploadSuccess(null), 3000);
      refetch();
    } catch {
      // Hata durumu
    } finally {
      setStampUploading(false);
    }
  }

  function handleDocDrop(e: React.DragEvent) {
    e.preventDefault();
    setDocDragOver(false);
    const _file = e.dataTransfer.files[0];
    if (_file) {
      // TODO: Client document upload API endpoint'i hazırlandığında burada yükleme işlevi aktif edilecek.
    }
  }

  function handleDocSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const _file = e.target.files?.[0];
    if (_file) {
      // TODO: Client document upload API endpoint'i hazırlandığında burada yükleme işlevi aktif edilecek.
    }
    e.target.value = "";
  }

  function formatFileSize(bytes: number) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  if (loading) return <PageLoading />;
  if (error) return <ErrorMessage message={error} onRetry={refetch} />;
  if (!data) return null;

  const client = data.data;
  const API_BASE = getApiBase();
  const logoSrc = client.logo_r2_key ? `${API_BASE}/clients/${client.id}/logo` : null;
  const stampSrc = client.stamp_r2_key ? `${API_BASE}/clients/${client.id}/stamp` : null;
  const clientTypeLabel =
    CLIENT_TYPES.find((t) => t.value === client.client_type)?.label ||
    "Potansiyel";

  return (
    <div className="space-y-6">
      {/* Back button */}
      <button
        onClick={() => navigate("/clients")}
        className="inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Müşterilere Dön
      </button>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Client info */}
        <div className="lg:col-span-2 space-y-6">
          <div className="rounded-xl border border-border bg-card p-6">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary">
                    <Building2 className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-xl font-semibold text-foreground">
                        {client.company_name}
                      </h2>
                      <span
                        className={cn(
                          "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
                          client.client_type === "active"
                            ? "bg-green-500/10 text-green-400 border border-green-500/20"
                            : "bg-amber-500/10 text-amber-400 border border-amber-500/20"
                        )}
                      >
                        {clientTypeLabel}
                      </span>
                    </div>
                    {client.sector && (
                      <p className="text-sm text-muted-foreground">
                        {client.sector}
                      </p>
                    )}
                  </div>
                </div>
                <div className="mt-4 space-y-1.5">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <FileText className="h-3.5 w-3.5" />
                    {client.contact_name}
                  </div>
                  {client.contact_email && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Mail className="h-3.5 w-3.5" />
                      <a
                        href={`mailto:${client.contact_email}`}
                        className="hover:text-accent"
                      >
                        {client.contact_email}
                      </a>
                    </div>
                  )}
                  {client.contact_phone && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Phone className="h-3.5 w-3.5" />
                      <a
                        href={`tel:${client.contact_phone}`}
                        className="hover:text-accent"
                      >
                        {client.contact_phone}
                      </a>
                    </div>
                  )}
                  {(client.city || client.district) && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <MapPin className="h-3.5 w-3.5" />
                      {[client.district, client.city]
                        .filter(Boolean)
                        .join(", ")}
                    </div>
                  )}
                  {client.tax_number && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <FileText className="h-3.5 w-3.5" />
                      Vergi No: {client.tax_number}
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="h-3.5 w-3.5" />
                    {formatDate(client.created_at)}
                  </div>
                </div>
              </div>
              <button
                onClick={startEditing}
                className="rounded-lg border border-border p-2 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
              >
                <Pencil className="h-4 w-4" />
              </button>
            </div>

            {client.notes && (
              <div className="mt-6 border-t border-border pt-4">
                <h3 className="text-sm font-medium text-foreground">Notlar</h3>
                <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground">
                  {client.notes}
                </p>
              </div>
            )}
          </div>

          {/* Client projects */}
          <div className="rounded-xl border border-border bg-card p-6">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-foreground">
                Projeler
              </h3>
              <button
                onClick={() =>
                  navigate(`/projects?new=1&client_id=${client.id}`)
                }
                className="inline-flex items-center gap-2 rounded-lg bg-accent px-3 py-1.5 text-sm font-medium text-accent-foreground transition-colors hover:bg-accent/90"
              >
                <Plus className="h-3.5 w-3.5" />
                Yeni Proje Oluştur
              </button>
            </div>

            {client.projects && client.projects.length > 0 ? (
              <div className="mt-4 space-y-3">
                {client.projects.map((project) => {
                  const statusInfo = PROJECT_STATUS[project.status] || {
                    label: project.status,
                    color: "text-muted-foreground",
                  };
                  return (
                    <div
                      key={project.id}
                      onClick={() => navigate(`/projects/${project.id}`)}
                      className="flex cursor-pointer items-center justify-between rounded-lg border border-border p-4 transition-colors hover:bg-secondary/50"
                    >
                      <div className="flex items-center gap-3">
                        <FolderKanban className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="text-sm font-medium text-foreground">
                            {project.title}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {PROGRAM_LABELS[project.program_type] ||
                              project.program_type}
                          </p>
                        </div>
                      </div>
                      <span
                        className={cn(
                          "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium",
                          statusInfo.color
                        )}
                      >
                        {statusInfo.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="mt-3 text-sm text-muted-foreground">
                Henüz proje bulunmuyor.
              </p>
            )}
          </div>

          {/* Client Documents Section */}
          <div className="rounded-xl border border-border bg-card p-6">
            <h3 className="text-base font-semibold text-foreground">
              Müşteri Belgeleri
            </h3>
            {/* TODO: Client document API endpoint'i hazırlandığında burada gerçek yükleme/listeleme/silme işlevleri aktif edilecek. */}

            {/* Upload area */}
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setDocDragOver(true);
              }}
              onDragLeave={() => setDocDragOver(false)}
              onDrop={handleDocDrop}
              onClick={() => docInputRef.current?.click()}
              className={cn(
                "mt-4 flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed p-6 transition-colors",
                docDragOver
                  ? "border-accent bg-accent/5"
                  : "border-border hover:border-accent/50 hover:bg-secondary/50"
              )}
            >
              <Upload className="h-6 w-6 text-muted-foreground" />
              <p className="mt-2 text-sm text-muted-foreground">
                Dosya yüklemek için tıklayın veya sürükleyin
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                Tüm dosya türleri kabul edilir
              </p>
              <input
                ref={docInputRef}
                type="file"
                className="hidden"
                onChange={handleDocSelect}
              />
            </div>

            {/* Document list */}
            {clientDocs.length > 0 ? (
              <div className="mt-4 rounded-lg border border-border">
                <div className="divide-y divide-border">
                  {clientDocs.map((doc) => (
                    <div
                      key={doc.id}
                      className="flex items-center justify-between p-3"
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-secondary">
                          <FileText className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground">
                            {doc.name}
                          </p>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <span>{formatFileSize(doc.size)}</span>
                            <span>-</span>
                            <span>{formatDate(doc.uploadedAt)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <a
                          href={doc.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                        >
                          <Download className="h-4 w-4" />
                        </a>
                        <button
                          onClick={() => {
                            // TODO: Client document silme API endpoint'i hazırlandığında burada silme işlevi aktif edilecek.
                          }}
                          className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="mt-4 text-sm text-muted-foreground">
                Henüz belge yüklenmemiş.
              </p>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Logo & Stamp */}
          <div className="rounded-xl border border-border bg-card p-4 space-y-4">
            <h3 className="text-sm font-medium text-foreground">
              Firma Görselleri
            </h3>
            {/* Upload success toast */}
            {uploadSuccess && (
              <div className="rounded-lg border border-green-500/20 bg-green-500/10 px-3 py-2 text-xs font-medium text-green-400">
                {uploadSuccess}
              </div>
            )}

            {/* Logo */}
            <div>
              <h4 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Firma Logosu
              </h4>
              {logoSrc ? (
                <div className="relative mt-2 flex items-center justify-center rounded-lg border border-border bg-secondary p-3">
                  <img
                    src={logoSrc}
                    alt={`${client.company_name} logosu`}
                    className="h-16 w-auto object-contain"
                  />
                  <label className="absolute right-2 top-2 cursor-pointer rounded-full bg-secondary p-1 text-muted-foreground hover:bg-accent/10 hover:text-accent">
                    <Upload className="h-3.5 w-3.5" />
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept={ACCEPTED_IMAGE_TYPES}
                      className="hidden"
                      onChange={(e) => {
                        handleLogoSelect(e.target.files?.[0] || null);
                        e.target.value = "";
                      }}
                    />
                  </label>
                </div>
              ) : (
                <label className={cn(
                  "mt-2 flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 border-dashed border-border p-4 transition-colors hover:border-accent/50 hover:bg-secondary/50",
                  logoUploading && "pointer-events-none opacity-50"
                )}>
                  {logoUploading ? (
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  ) : (
                    <Upload className="h-5 w-5 text-muted-foreground" />
                  )}
                  <span className="text-xs text-muted-foreground">
                    JPG, PNG, SVG veya WebP
                  </span>
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept={ACCEPTED_IMAGE_TYPES}
                    className="hidden"
                    onChange={(e) => {
                      handleLogoSelect(e.target.files?.[0] || null);
                      e.target.value = "";
                    }}
                  />
                </label>
              )}
            </div>

            {/* Stamp */}
            <div>
              <h4 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Firma Kaşesi
              </h4>
              {stampSrc ? (
                <div className="relative mt-2 flex items-center justify-center rounded-lg border border-border bg-secondary p-3">
                  <img
                    src={stampSrc}
                    alt={`${client.company_name} kaşesi`}
                    className="h-16 w-auto object-contain"
                  />
                  <label className="absolute right-2 top-2 cursor-pointer rounded-full bg-secondary p-1 text-muted-foreground hover:bg-accent/10 hover:text-accent">
                    <Upload className="h-3.5 w-3.5" />
                    <input
                      ref={stampInputRef}
                      type="file"
                      accept={ACCEPTED_IMAGE_TYPES}
                      className="hidden"
                      onChange={(e) => {
                        handleStampSelect(e.target.files?.[0] || null);
                        e.target.value = "";
                      }}
                    />
                  </label>
                </div>
              ) : (
                <label className={cn(
                  "mt-2 flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 border-dashed border-border p-4 transition-colors hover:border-accent/50 hover:bg-secondary/50",
                  stampUploading && "pointer-events-none opacity-50"
                )}>
                  {stampUploading ? (
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  ) : (
                    <Upload className="h-5 w-5 text-muted-foreground" />
                  )}
                  <span className="text-xs text-muted-foreground">
                    JPG, PNG, SVG veya WebP
                  </span>
                  <input
                    ref={stampInputRef}
                    type="file"
                    accept={ACCEPTED_IMAGE_TYPES}
                    className="hidden"
                    onChange={(e) => {
                      handleStampSelect(e.target.files?.[0] || null);
                      e.target.value = "";
                    }}
                  />
                </label>
              )}
            </div>
          </div>

          <div className="rounded-xl border border-border bg-card p-4">
            <h3 className="text-sm font-medium text-foreground">
              Hızlı İşlemler
            </h3>
            <div className="mt-3 space-y-2">
              {client.contact_email && (
                <a
                  href={`mailto:${client.contact_email}`}
                  className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                >
                  <Mail className="h-3.5 w-3.5" />
                  E-posta Gönder
                </a>
              )}
              {client.contact_phone && (
                <a
                  href={`tel:${client.contact_phone}`}
                  className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                >
                  <Phone className="h-3.5 w-3.5" />
                  Ara
                </a>
              )}
              <button
                onClick={() =>
                  navigate(`/projects?new=1&client_id=${client.id}`)
                }
                className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
              >
                <Plus className="h-3.5 w-3.5" />
                Yeni Proje Oluştur
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Client */}
      <div className="rounded-xl border border-destructive/30 bg-card p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-destructive">Tehlikeli Alan</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Bu müşteriyi kalıcı olarak silmek için aşağıdaki butona tıklayın.
            </p>
          </div>
          <button
            onClick={() => { setShowDeleteConfirm(true); setDeleteError(""); }}
            className="rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-2 text-sm font-medium text-destructive transition-colors hover:bg-destructive/20"
          >
            Müşteriyi Sil
          </button>
        </div>
      </div>

      {/* Delete Confirm Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowDeleteConfirm(false)} />
          <div className="relative z-10 w-full max-w-sm rounded-xl border border-border/60 bg-card p-6 shadow-2xl ring-1 ring-white/5">
            <h2 className="text-lg font-semibold text-foreground">Müşteriyi Sil</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              <strong>{client.company_name}</strong> müşterisini silmek istediğinize emin misiniz? Bu işlem geri alınamaz.
            </p>
            {deleteError && (
              <div className="mt-3 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                {deleteError}
              </div>
            )}
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
              >
                İptal
              </button>
              <button
                onClick={handleDeleteClient}
                disabled={deleting}
                className={cn(
                  "inline-flex items-center gap-2 rounded-lg bg-destructive px-4 py-2 text-sm font-medium text-destructive-foreground transition-colors hover:bg-destructive/90",
                  deleting && "cursor-not-allowed opacity-50"
                )}
              >
                {deleting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Evet, Sil
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setEditing(false)}
          />
          <div className="relative z-10 w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-xl border border-border/60 bg-card p-6 shadow-2xl ring-1 ring-white/5">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-foreground">
                Müşteriyi Düzenle
              </h2>
              <button
                onClick={() => setEditing(false)}
                className="rounded-lg p-1 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleSave} className="mt-4 space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-foreground">
                    Firma Adı *
                  </label>
                  <input
                    type="text"
                    required
                    value={form.company_name}
                    onChange={(e) =>
                      handleFormChange("company_name", e.target.value)
                    }
                    className="h-9 w-full rounded-lg border border-border bg-secondary px-3 text-sm text-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-foreground">
                    İlgili Kişi *
                  </label>
                  <input
                    type="text"
                    required
                    value={form.contact_name}
                    onChange={(e) =>
                      handleFormChange("contact_name", e.target.value)
                    }
                    className="h-9 w-full rounded-lg border border-border bg-secondary px-3 text-sm text-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-foreground">
                    E-posta
                  </label>
                  <input
                    type="email"
                    value={form.contact_email}
                    onChange={(e) =>
                      handleFormChange("contact_email", e.target.value)
                    }
                    className="h-9 w-full rounded-lg border border-border bg-secondary px-3 text-sm text-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-foreground">
                    Telefon
                  </label>
                  <input
                    type="tel"
                    value={form.contact_phone}
                    onChange={(e) =>
                      handleFormChange("contact_phone", e.target.value)
                    }
                    className="h-9 w-full rounded-lg border border-border bg-secondary px-3 text-sm text-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-foreground">
                    Şehir
                  </label>
                  <input
                    type="text"
                    value={form.city}
                    onChange={(e) =>
                      handleFormChange("city", e.target.value)
                    }
                    className="h-9 w-full rounded-lg border border-border bg-secondary px-3 text-sm text-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-foreground">
                    İlçe
                  </label>
                  <input
                    type="text"
                    value={form.district}
                    onChange={(e) =>
                      handleFormChange("district", e.target.value)
                    }
                    className="h-9 w-full rounded-lg border border-border bg-secondary px-3 text-sm text-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-foreground">
                    Müşteri Türü
                  </label>
                  <select
                    value={form.client_type}
                    onChange={(e) =>
                      handleFormChange("client_type", e.target.value)
                    }
                    className="h-9 w-full rounded-lg border border-border bg-secondary px-3 text-sm text-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                  >
                    {CLIENT_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-foreground">
                    Vergi No
                  </label>
                  <input
                    type="text"
                    value={form.tax_number}
                    onChange={(e) =>
                      handleFormChange("tax_number", e.target.value)
                    }
                    maxLength={11}
                    className="h-9 w-full rounded-lg border border-border bg-secondary px-3 text-sm text-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                  />
                </div>
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-foreground">
                  Notlar
                </label>
                <textarea
                  value={form.notes}
                  onChange={(e) => handleFormChange("notes", e.target.value)}
                  rows={3}
                  className="w-full rounded-lg border border-border bg-secondary px-3 py-2 text-sm text-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                />
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setEditing(false)}
                  className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className={cn(
                    "inline-flex items-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-accent-foreground transition-colors hover:bg-accent/90",
                    saving && "cursor-not-allowed opacity-50"
                  )}
                >
                  {saving && (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  )}
                  Kaydet
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
